import { ActionIcon, Button, Group, Progress, Stack, Text } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import cx from 'clsx'
import { observer } from 'mobx-react-lite'

import { useGlobalState } from '@/hooks/useGlobalState'
import { confirmStop } from '@/screens/BulkContentUpload/bulkUploadConfirmations'
import { bannerFinishedSubline, bannerRunningSubline, bulkJobCopy } from '@/screens/BulkContentUpload/bulkUploadJobCopy'
import * as classes from '@/styles/BulkUploadBanner.css'

/** Decision 7, less Paused (49), with decision 50's summary in place of Interrupted. */
export const BulkUploadBanner = observer(function BulkUploadBanner() {
  const { bulkUploadStore: store } = useGlobalState()
  const job = store.job
  if (!job) return null

  const copy = bulkJobCopy[job.mode].banner
  const viewDetails = (
    <Button variant="default" size="xs" onClick={() => store.setPanelOpen(true)}>
      View Details
    </Button>
  )

  let tone: keyof typeof classes.tone
  let headline: string
  let subline: string | undefined
  let actions: React.ReactNode

  if (job.state === 'running') {
    tone = 'indigo'
    headline = copy.running(job)
    subline = bannerRunningSubline(job.percent)
    actions = (
      <>
        {viewDetails}
        {job.waiting > 0 && (
          <Button variant="outline" color="red" size="xs" onClick={() => confirmStop(store)}>
            Stop
          </Button>
        )}
      </>
    )
  } else if (job.state === 'interrupted') {
    tone = 'amber'
    headline = copy.interrupted(job)
    actions = (
      <>
        {viewDetails}
        <Button variant="default" size="xs" onClick={() => store.dismiss()}>
          Dismiss
        </Button>
      </>
    )
  } else {
    const failed = job.failed > 0
    tone = failed ? 'red' : 'green'
    headline = failed ? copy.finishedWithFailures(job) : copy.finished(job)
    subline = bannerFinishedSubline
    actions = (
      <>
        {failed && (
          <Button variant="default" size="xs" onClick={() => void store.retryFailed()}>
            Retry {job.failed}
          </Button>
        )}
        {viewDetails}
        <ActionIcon variant="subtle" color="gray" aria-label="Dismiss" onClick={() => store.dismiss()}>
          <IconX size={16} aria-hidden="true" />
        </ActionIcon>
      </>
    )
  }

  return (
    <section className={cx(classes.banner, classes.tone[tone])} aria-label="Bulk upload">
      <Stack gap={2} className={classes.text}>
        {/* Only the headline is live: the subline's percent would be re-announced on every tick. */}
        <Text size="sm" fw={600} role="status">
          {headline}
        </Text>
        {subline && (
          <Text size="xs" c="dimmed">
            {subline}
          </Text>
        )}
      </Stack>
      {job.state === 'running' && (
        <Progress
          className={classes.progress}
          value={job.percent}
          striped
          animated
          aria-label="Overall upload progress"
        />
      )}
      <Group gap="xs" wrap="nowrap" className={classes.actions}>
        {actions}
      </Group>
    </section>
  )
})
